var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};

import { action } from 'mobx';
import { NotFound } from '../errors';
import Navigation from '../model/Navigation';
import differenceWith from '../util/differenceWith';
import isUrlFullyMatched from './util/isUrlFullyMatched';
import areRoutesEqual from '../model/util/areRoutesEqual';
import TransitionManager from '../transition/TransitionManager';
import { EventTypes } from '../events';
import { RouteError } from '../errors';
/*
 * This function maps an Event to a new Event. It is used by the Scheduler to manage data flow.
 */

export default function maybeProcessEvent(evt, store) {
  try {
    return processEvent(evt, store).then(function (next) {
      if (next) {
        if (store.isCancelled(next.navigation)) {
          return {
            type: EventTypes.EMPTY,
            navigation: null
          };
        } else {
          // Any setters returned after willResolve is called here.
          if (typeof next.setter === 'function') {
            next.setter();
          }

          return next;
        }
      } else {
        return {
          type: EventTypes.EMPTY,
          navigation: null
        };
      }
    }).catch(function (err) {
      return {
        type: EventTypes.NAVIGATION_ERROR,
        navigation: evt && evt.navigation,
        error: err,
        done: true
      };
    });
  } catch (err) {
    return Promise.resolve({
      type: EventTypes.NAVIGATION_ERROR,
      navigation: evt && evt.navigation,
      error: err,
      done: true
    });
  }
}

function normalizePath(x) {
  return x.endsWith('/') ? x : x + '/';
}

export function processEvent(evt, store) {
  switch (evt.type) {
    case EventTypes.NAVIGATION_START:
      {
        var navigation = evt.navigation;
        var matchedPath = store.state.pathFromRoot(normalizePath(navigation.to.pathname));
        return Promise.resolve({
          type: EventTypes.NAVIGATION_RESULT_MATCHED,
          navigation: navigation,
          matchedPath: matchedPath
        });
      }

    case EventTypes.NAVIGATION_RETRY:
      {
        var leaf = evt.leaf,
            partialPath = evt.partialPath;
        var restOfPathElements = store.state.pathFrom(leaf.node, '' + leaf.segment + leaf.remaining, leaf.params, leaf.parentUrl);

        var _matchedPath = partialPath.slice().concat(restOfPathElements.slice(1));

        return Promise.resolve({
          type: EventTypes.NAVIGATION_RESULT_MATCHED,
          navigation: evt.navigation,
          matchedPath: _matchedPath
        });
      }

    case EventTypes.NAVIGATION_RESULT_MATCHED:
      {
        var _matchedPath2 = evt.matchedPath,
            _navigation = evt.navigation;
        var _leaf = _matchedPath2[_matchedPath2.length - 1];
        var isFullyMatched = isUrlFullyMatched(_navigation.to.pathname, _matchedPath2);
        var loader = _leaf && _leaf.node.value.loadChildren;

        if (!isFullyMatched && typeof loader !== 'function') {
          var caughtPath = findCatchAllPath(_matchedPath2);

          if (caughtPath.length > 0) {
            return Promise.resolve({
              type: EventTypes.NAVIGATION_ACTIVATING,
              navigation: evt.navigation,
              partialPath: caughtPath,
              routes: store.getNextRoutes(caughtPath, _navigation.to)
            });
          }

          return Promise.resolve({
            type: EventTypes.NAVIGATION_ERROR,
            navigation: _navigation,
            error: new NotFound(_navigation),
            done: true
          });
        }

        if (typeof loader === 'function' && _leaf.node.children.length === 0) {
          return Promise.resolve({
            type: EventTypes.CHILDREN_CONFIG_REQUESTED,
            navigation: evt.navigation,
            partialPath: _matchedPath2,
            leaf: _leaf,
            loader: loader
          });
        }

        if (isCatchAll(_leaf && _leaf.node)) {
          store.updateError(new NotFound(_navigation));
        }

        return Promise.resolve({
          type: EventTypes.NAVIGATION_ACTIVATING,
          navigation: evt.navigation,
          partialPath: _matchedPath2,
          routes: store.getNextRoutes(_matchedPath2, _navigation.to)
        });
      }

    case EventTypes.CHILDREN_CONFIG_REQUESTED:
      {
        var _navigation2 = evt.navigation,
            _leaf2 = evt.leaf,
            _partialPath = evt.partialPath,
            _loader = evt.loader;
        return _loader().then(function (module) {
          return {
            type: EventTypes.CHILDREN_CONFIG_LOADED,
            navigation: _navigation2,
            partialPath: _partialPath,
            leaf: _leaf2,
            module: module
          };
        });
      }

    case EventTypes.CHILDREN_CONFIG_LOADED:
      {
        var _navigation3 = evt.navigation,
            _partialPath2 = evt.partialPath,
            _leaf3 = evt.leaf,
            module = evt.module;
        var children = _typeof(module.default) === 'object' ? module.default : module;

        if (children.length) {
          return Promise.resolve({
            type: EventTypes.CHILDREN_LOADING,
            navigation: _navigation3,
            partialPath: _partialPath2,
            root: store.state.root,
            leaf: _leaf3,
            children: children
          });
        } else {
          return Promise.resolve({
            type: EventTypes.NAVIGATION_ERROR,
            navigation: _navigation3,
            error: new Error('Dynamic children function must resolve to an array.'),
            done: true
          });
        }
      }

    case EventTypes.CHILDREN_LOADING:
      {
        var _navigation4 = evt.navigation,
            _partialPath3 = evt.partialPath,
            _leaf4 = evt.leaf,
            _children = evt.children;
        return Promise.resolve({
          type: EventTypes.CHILDREN_LOADED,
          navigation: _navigation4,
          partialPath: _partialPath3,
          leaf: _leaf4,
          root: store.state.root,
          setter: action(function () {
            store.replaceChildren(_leaf4.node, _children);
            _leaf4.node.value.loadChildren = null;
          })
        });
      }

    case EventTypes.CHILDREN_LOADED:
      {
        var _navigation5 = evt.navigation,
            _partialPath4 = evt.partialPath,
            _leaf5 = evt.leaf;

        if (_navigation5 && _partialPath4) {
          return Promise.resolve({
            type: EventTypes.NAVIGATION_RETRY,
            navigation: _navigation5,
            partialPath: _partialPath4,
            leaf: _leaf5
          });
        } else {
          return Promise.resolve({
            type: EventTypes.EMPTY,
            navigation: evt.navigation
          });
        }
      }

    case EventTypes.NAVIGATION_ACTIVATING:
      {
        var _navigation6 = evt.navigation,
            routes = evt.routes,
            _partialPath5 = evt.partialPath;
        var currRoutes = store.routes.slice(); // We've found a match or unmatched error has been handled.

        var _diffRoutes = diffRoutes(currRoutes, routes),
            activating = _diffRoutes.activating,
            deactivating = _diffRoutes.deactivating,
            entering = _diffRoutes.entering,
            exiting = _diffRoutes.exiting; // Add matched leaf to the navigation object so it can be used for redirection


        _navigation6.leaf = routes[routes.length - 1];
        return evalTransitionsForRoutes([{
          type: 'canDeactivate'
        }, {
          type: 'willDeactivate'
        }], deactivating, _navigation6).then(function () {
          return evalTransitionsForRoutes([{
            type: 'canActivate',
            includes: activating
          }, {
            type: 'willActivate',
            includes: activating
          }, {
            type: 'willResolve',
            includes: entering
          }], routes, _navigation6).then(function (setter) {
            return {
              type: EventTypes.NAVIGATION_ACTIVATED,
              navigation: _navigation6,
              activating: activating,
              deactivating: deactivating,
              entering: entering,
              exiting: exiting,
              routes: routes,
              setter: setter
            };
          });
        }).catch(function (err) {
          store.updateError(err); // Navigation error may be thrown by a guard or lifecycle hook.
          // If so, mark the current navigation as cancelled, and use the error Navigation as next.

          if (err instanceof Navigation) {
            return {
              type: EventTypes.NAVIGATION_CANCELLED,
              navigation: evt.navigation,
              nextNavigation: err,
              done: true
            };
          } else if (err instanceof RouteError) {
            var _caughtPath = findCatchAllPath(_partialPath5);

            if (_caughtPath.length > 0) {
              return {
                type: EventTypes.NAVIGATION_ACTIVATING,
                navigation: _navigation6,
                partialPath: _caughtPath,
                routes: store.getNextRoutes(_caughtPath, _navigation6.to)
              };
            }
          }

          return {
            type: EventTypes.NAVIGATION_ERROR,
            navigation: _navigation6,
            error: err,
            done: true
          };
        });
      }

    case EventTypes.NAVIGATION_ACTIVATED:
      {
        var _navigation7 = evt.navigation,
            _routes = evt.routes,
            _exiting = evt.exiting,
            _entering = evt.entering;
        return Promise.resolve({
          type: EventTypes.NAVIGATION_TRANSITION_START,
          navigation: evt.navigation,
          routes: _routes,
          entering: _entering,
          exiting: _exiting,
          setter: action(function () {
            store.updateRoutes(_routes);
            store.updateLocation(_navigation7.to);
          })
        });
      }

    case EventTypes.NAVIGATION_TRANSITION_START:
      {
        var _navigation8 = evt.navigation,
            _routes2 = evt.routes,
            _exiting2 = evt.exiting,
            _entering2 = evt.entering;
        var done = void 0;

        if (_navigation8.shouldTransition) {
          // Run and wait on transition of exiting and newly entering nodes.
          done = Promise.all([TransitionManager.run('exiting', _exiting2), TransitionManager.run('entering', _entering2)]);
        } else {
          done = Promise.resolve();
        }

        return done.then(function () {
          return {
            type: EventTypes.NAVIGATION_TRANSITION_END,
            navigation: _navigation8,
            routes: _routes2,
            entering: _entering2,
            exiting: _exiting2
          };
        });
      }

    case EventTypes.NAVIGATION_TRANSITION_END:
      {
        var _exiting3 = evt.exiting,
            _entering3 = evt.entering;

        _exiting3.forEach(function (route) {
          return typeof route.node.value.onExit === 'function' && route.node.value.onExit(route);
        });

        _entering3.forEach(function (route) {
          return typeof route.node.value.onEnter === 'function' && route.node.value.onEnter(route);
        });

        return Promise.resolve({
          type: EventTypes.NAVIGATION_END,
          navigation: evt.navigation,
          routes: _entering3,
          done: true
        });
      }

    case EventTypes.NAVIGATION_ERROR:
      {
        return Promise.resolve(null);
      }

    case EventTypes.NAVIGATION_CANCELLED:
      {
        return Promise.resolve({
          type: EventTypes.EMPTY,
          navigation: evt.navigation,
          setter: function setter() {
            store.cancel(evt.navigation);
            store.clearPrevRoutes();
          }
        });
      }

    case EventTypes.NAVIGATION_END:
      {
        var _routes3 = evt.routes;

        var _leaf6 = _routes3 && _routes3[_routes3.length - 1];

        return Promise.resolve({
          type: EventTypes.EMPTY,
          navigation: evt.navigation,
          setter: function setter() {
            if (!isCatchAll(_leaf6 && _leaf6.node)) {
              store.updateError(null);
            }

            store.clearPrevRoutes();
          }
        });
      }

    default:
      return Promise.resolve(evt);
  }
}
/*
 * Private helpers.
 */

function diffRoutes(currRoutes, nextRoutes) {
  var parentWillResolve = void 0; // Exiting this specific route instance

  var exiting = currRoutes.reduce(function (acc, x, idx) {
    var y = nextRoutes.length > idx ? nextRoutes[idx] : undefined;

    if (acc.length > 0 || !areRoutesEqual(x, y)) {
      acc.push(x);
    }

    return acc;
  }, []).reverse(); // Entering this specific route instance

  var entering = nextRoutes.reduce(function (acc, x, idx) {
    var y = currRoutes.length > idx ? currRoutes[idx] : undefined;

    if (acc.length > 0 || !areRoutesEqual(x, y)) {
      acc.push(x);
    }

    return acc;
  }, []); // Deactivating this route state tree node

  parentWillResolve = false;
  var deactivating = currRoutes.reduce(function (acc, x, idx) {
    var y = nextRoutes.length > idx ? nextRoutes[idx] : undefined;

    if (acc.length > 0 || parentWillResolve || x.node !== (y && y.node)) {
      acc.push(x);
    }

    if (!areRoutesEqual(x, y)) {
      parentWillResolve = true;
    }

    return acc;
  }, []).reverse(); // Activating this route state tree node

  parentWillResolve = false;
  var activating = nextRoutes.reduce(function (acc, x, idx) {
    var y = currRoutes.length > idx ? currRoutes[idx] : undefined;

    if (acc.length > 0 || parentWillResolve || x.node !== (y && y.node)) {
      acc.push(x);
    }

    if (!areRoutesEqual(x, y)) {
      parentWillResolve = true;
    }

    return acc;
  }, []);
  return {
    activating: activating,
    deactivating: deactivating,
    entering: entering,
    exiting: exiting
  };
}

function isIncluded(route, routes) {
  return routes ? routes.includes(route) : true;
}

function evalTransition(operation, route, navigation, state) {
  var value = route.node.value;
  var type = operation.type,
      includes = operation.includes;
  var result = typeof value[type] === 'function' && isIncluded(route, includes) ? value[type](route, navigation) : true; // If the guard results in `false` or a rejected promise then mark transition as failed.

  if (false === result) {
    return navigation.goBack(); // TODO: This `goBack` will not work if this is the first navigation in the system.
  } else if (typeof result.then === 'function') {
    return result.then(function (x) {
      if (typeof x === 'function') {
        state.setters.push(x);
      }
    }).catch(function (err) {
      if (typeof route.node.value.onError === 'function') {
        var _result = route.node.value.onError(route, navigation, err);

        if (typeof _result.then === 'function') {
          return _result.then(function () {
            state.caughtError = true;
          });
        }
      }

      return Promise.reject(err);
    });
  } else {
    return Promise.resolve();
  }
}

function evalTransitions(operations, route, navigation, state) {
  return operations.reduce(function (curr, operation) {
    return state.caughtError ? Promise.resolve() : curr.then(function () {
      return evalTransition(operation, route, navigation, state);
    });
  }, Promise.resolve());
}

function evalTransitionsForRoutes(operations, routes, navigation) {
  var state = {
    caughtError: false,
    setters: []
  };
  return routes.reduce(function (curr, route) {
    return state.caughtError ? Promise.resolve() : curr.then(function () {
      return evalTransitions(operations, route, navigation, state);
    });
  }, Promise.resolve()).then(function () {
    return function () {
      return state.setters.forEach(function (f) {
        return f();
      });
    };
  });
}

function isCatchAll(node) {
  return !!(node && node.value.path === '**');
}

function findCatchAllPath(path) {
  var leaf = path[path.length - 1];

  if (!leaf) {
    return [];
  }

  var idx = path.length - 1;
  var foundNode = void 0;

  while (idx >= 0) {
    var element = path[idx];
    foundNode = element.node.children.find(isCatchAll);

    if (foundNode) {
      break;
    }

    idx--;
  }

  if (foundNode) {
    return path.slice(0, idx).concat([{
      node: foundNode,
      parentUrl: leaf.parentUrl,
      segment: leaf.remaining,
      remaining: '',
      params: {}
    }]);
  }

  return [];
}